import { MenuItem, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useMeasure } from 'react-use';
import { makeStyles } from 'tss-react/mui';
import { notNullOrEmpty } from '../../utils/common';
import { StylesProps } from '../emobilityEngineSelect/stylesHelpers';
import { ResponsiveSelect, ResponsiveSelectProps } from '../responsiveSelect';
import { EmobilityBatteryMenuItemContent, EmobilityBatteryMenuItemContentProps } from './emobilityBatteryValueItemContent';
import { fromSize } from './stylesHelpers';
import { Battery, Level } from './types';

const useStyles = makeStyles<StylesProps>({ name: 'EmobilityBatterySelect' })((theme, props) => ({
	select: {
		paddingTop: 0,
		paddingBottom: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		'&.MuiSelect-select': {
			height: fromSize({ '0': 56, '275': 83 })(props)
		}
	},
	icon: {
		top: '50%',
		right: fromSize({ '0': 16, '275': 24 })(props)
	},
	menuItem: {
		alignItems: 'flex-start',
		'&.mobile-screen': {
			paddingRight: 28,
			paddingLeft: 28,
			paddingTop: 13,
			paddingBottom: 13,
			minHeight: 'unset'
		},
		'&.desktop-screen': {
			paddingTop: 19,
			paddingBottom: 19
		}
	}
}));

type OmitProps = 'children' | 'onChange' | 'value';

export type EmobilityBatterySelectProps = Omit<ResponsiveSelectProps, OmitProps> & {
	items: Battery[];
	value: Level;
	labelCharging: string;
	onChange: (value: Level) => void;
	onItemClick?: (item: Battery, index: number) => void;
};

export const EmobilityBatterySelect: React.FC<EmobilityBatterySelectProps> = (props) => {
	const { items, value, onChange, labelCharging, onItemClick, classes, dark, ...other } = props;
	const theme = useTheme();
	const isMobileScreen = useMediaQuery(theme.originalBreakpoints.only('xs'));
	const [selectRef, { width: selectSize }] = useMeasure();

	let { classes: styles, cx } = useStyles({ selectSize }, { props });

	if (!notNullOrEmpty(items)) {
		return null;
	}

	const createMobileScreenMenuItemProps = (battery: Battery) => ({
		iconVariant: 'none',
		valueVariant: 'large',
		...battery
	} as EmobilityBatteryMenuItemContentProps);

	const createDesktopScreenMenuItemProps = (battery: Battery) => ({
		iconVariant: 'small',
		valueVariant: 'small',
		labelCharging,
		...battery
	} as EmobilityBatteryMenuItemContentProps);

	const createMenuItemProps = (engine: Battery) =>
		isMobileScreen
			? createMobileScreenMenuItemProps(engine)
			: createDesktopScreenMenuItemProps(engine);

	const crateValueProps = (battery: Battery) => ({
		iconVariant: 'large',
		valueVariant: fromSize({ '0': 'small', '822': 'small-wide' })({ selectSize }),
		labelCharging,
		level: battery.level
	} as EmobilityBatteryMenuItemContentProps);

	const menuItems = items.map((item, index) =>
		<MenuItem
			data-test={`menu-item-${item.level}`}
			key={item.level} value={item.level}
			className={cx(styles.menuItem, isMobileScreen ? 'mobile-screen' : 'desktop-screen')}
			onClick={() => onItemClick?.(item, index)}>
			<EmobilityBatteryMenuItemContent {...createMenuItemProps(item)} />
		</MenuItem>);

	const renderValue = (level: Level) => {
		const item = items.find(i => i.level === level);
		const props = crateValueProps(item);
		return <EmobilityBatteryMenuItemContent {...props} />;
	};

	const DesktopMenuProps = {
		...theme.components.MuiSelect.defaultProps.MenuProps,
		PaperProps: {
			...theme.components.MuiSelect.defaultProps.MenuProps.PaperProps,
			style: { width: selectSize }
		}
	};

	return (
		<ResponsiveSelect
			data-test='emobility-battery-select'
			ref={selectRef}
			classes={{
				...classes,
				select: styles.select,
				iconFilled: styles.icon
			}}
			value={value}
			onChange={(ev) => onChange(ev.target.value as Level)}
			renderValue={renderValue}
			variant='filled'
			dark={dark}
			MenuProps={isMobileScreen ? undefined : DesktopMenuProps}
			{...other}>
			{menuItems}
		</ResponsiveSelect>
	);
};

EmobilityBatterySelect.defaultProps = {
	dark: true
};
